import { Alert, Card, CardContent, CardHeader, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatTime } from "~/modules/app/util/formatters";
import { GaiaMiniMap } from "~/modules/map/components/GaiaMiniMap";
import MediaList from "~/modules/media/components/MediaList/MediaList";
import RenderIf from "~/shared/components/RenderIf";

import { UsePlannerVisitWorkerReportShape } from "./usePlannerVisitWorkerReport";

interface ObservationListItemProps {
    observation: NonNullable<UsePlannerVisitWorkerReportShape["observations"]>[number];
}

export function ObservationListItem({ observation }: ObservationListItemProps) {
    const { t } = useTranslation();

    // TODO: Replace with better implementation based on SettingsForm

    // const observationTypeId = observation.observationTypeId as string;
    // const { data: observationQuestionAnswers } = useObservationAnswers(observationTypeId, observation.answers ?? {});
    const createdAt = parseStringToDate(observation.createdAt);

    const echometerFileStr = (observation.answers?.["echometer-recording"] as string) ?? null;
    const echometerFileArr = (echometerFileStr !== null ? echometerFileStr.split(",") : []).filter(x => x.length > 0);

    return (
        <Card component="li" variant="outlined">
            <CardHeader action={<Typography>{formatTime(createdAt)}</Typography>} title={observation.researchType?.name} />
            <CardContent sx={{ rowGap: 2, display: "flex", flexDirection: "column" }}>
                <RenderIf
                    payload={observation.feedbackComment}
                    render={value => {
                        if (value === null) {
                            return null;
                        }

                        return (
                            <Alert severity="info">
                                <Typography>
                                    {t("FieldworkerFeedbackScreen.ObservationListItem.feedback", { ns: "fieldworker" })}:
                                </Typography>
                                {value}
                            </Alert>
                        );
                    }}
                />

                <Stack direction="row" columnGap={1}>
                    <GaiaMiniMap geometry={observation.feature?.geometry} sx={{ borderRadius: 2, height: 125, width: 175 }} />

                    <Stack rowGap={1 / 4}>
                        {Object.entries(observation.preciseLocation).map(([key, value]) => (
                            <Typography key={key} variant="body2">
                                <strong>{t(`observationPreciseLocationForm.${key}`, { ns: "observation" })}</strong>{" "}
                                {value as string}
                            </Typography>
                        ))}

                        {/* {observationQuestionAnswers.map(field => (
                            <Typography key={field.id} variant="body2">
                                <strong>{field.label}:</strong> {field.answer}
                            </Typography>
                        ))} */}
                    </Stack>
                </Stack>

                <Stack direction="row" columnGap={1}>
                    {(observation.medias?.length ?? 0) > 0 && (
                        <Card variant="outlined" sx={{ flex: 1 / 2, padding: 2 }}>
                            <Typography>
                                {t("FieldworkerFeedbackScreen.ObservationListItem.medias", { ns: "fieldworker" })}
                            </Typography>
                            <Stack direction="row" columnGap={1}>
                                <MediaList medias={observation.medias ?? []} />
                            </Stack>
                        </Card>
                    )}

                    {echometerFileArr.length > 0 && (
                        <Card variant="outlined" sx={{ flex: 1 / 2, padding: 2 }}>
                            <Typography>
                                {t("FieldworkerFeedbackScreen.ObservationListItem.echometer", { ns: "fieldworker" })}
                            </Typography>
                            {/* <AudioFilePreview deleteable={false} mediaIds={echometerFileArr} /> TODO: Fix audio file preview component */}
                        </Card>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
